import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  SecurityContext,
  ViewChild,
  ViewChildren,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { faStop } from '@fortawesome/free-solid-svg-icons';
import { faCircle, faPause } from '@fortawesome/pro-solid-svg-icons';
import { Subject, merge } from 'rxjs';
import { map } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { TimeFormatType } from '../../../../core/pipes/time-format.pipe';
import { pageMap } from '../../../../core/routing/mapping/page-mapping';
import { PageKey } from '../../../../core/routing/page-keys';
import { RouteInfo, SIDEBAR_ROUTES } from '../../../../core/routing/routes/sidebar.routes';
import { SharedClockHandlerService } from '../../../../core/services/features/time-tracking/shared-clock-handler.service';
import { SharedTimerHandlerService } from '../../../../core/services/features/time-tracking/shared-timer-handler.service';
import {
  SharedTrackerHandlerService,
  TrackerStatus,
} from '../../../../core/services/features/time-tracking/shared-tracker-handler.service';
import { ShiveRenderService } from '../../../../core/services/rendering/shive-render.service';
import { ImageService } from '../../../../core/services/ui/image.service';

import { SidebarService } from '../../../../core/services/ui/sidebar.service';
import { ClockStatus, TimerStatus } from '../../../../features/calendar/calendar-types';
import { SharedActiveTimeBlockService } from '../../../../features/calendar/components/time-block/active/shared-active-time-block.service';
import { ClockData, TimeBlockSource } from '../../../../shared/data-types/shared-time-block-types';
import { sidebarCompressedWidth, sidebarEnlargedWidth } from './sidebar.config';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('parentNavItem') public parentNavItem!: ElementRef<HTMLElement>;
  @ViewChildren('parentListItem') public parentListItem!: QueryList<ElementRef<HTMLElement>>;
  @Input() public aside!: HTMLElement;
  @Input() public mainPanel!: HTMLElement;
  @Input() public events: Subject<TransitionEvent>;
  public clockData = signal<ClockData | null>(null);
  public trackerActive = signal<boolean | null>(null);
  public trackerStatus = signal<TrackerStatus>(TrackerStatus.None);
  public timerActive = signal<boolean | null>(null);
  public menuItems: RouteInfo[];

  // Constants
  public SecurityContext = SecurityContext;
  public readonly PageKey = PageKey;
  public readonly pageMap = pageMap;
  public readonly TimerStatus = TimerStatus;
  public readonly TimeFormatType = TimeFormatType;
  public readonly TrackerStatus = TrackerStatus;
  public readonly faPause = faPause;
  public readonly faCircle = faCircle;
  public readonly faStop = faStop;

  private readonly subs = new SubSink();

  constructor(
    @Inject(DOCUMENT) public document: Document,
    public imageService: ImageService,
    public sidebarService: SidebarService,
    private readonly router: Router,
    private readonly renderer: ShiveRenderService,
    private readonly sharedClockHandlerService: SharedClockHandlerService,
    private readonly sharedTrackerHandlerService: SharedTrackerHandlerService,
    private readonly sharedTimerHandlerService: SharedTimerHandlerService,
  ) {}

  ngOnInit(): void {
    this.sidebarService.sidebarCompressedWidth = sidebarCompressedWidth;
    this.sidebarService.sidebarEnlargedWidth = sidebarEnlargedWidth;
    this.sidebarService.setRouteData(this.router.url);
    this.menuItems = SIDEBAR_ROUTES.filter((menuItem) => menuItem);

    this.subs.sink = this.sharedClockHandlerService.activeClockChanged$.subscribe(
      (clockData: ClockData | null) => {
        if (clockData === null) {
          this.clockData.set([0, ClockStatus.NotTicking]);
          return;
        }
        this.clockData.set(clockData);
      },
    );

    // Timer
    const timerSub$ = this.sharedTimerHandlerService.timerActiveTBChanged$.pipe(
      map((value) => ({ source: TimeBlockSource.TimerSource, value })),
    );

    // Tracker
    const trackerSub$ = this.sharedTrackerHandlerService.trackerStatus$.pipe(
      map((value) => ({ source: TimeBlockSource.TrackerSource, value })),
    );

    this.subs.sink = merge(timerSub$, trackerSub$).subscribe((data) => {
      if (data.source === TimeBlockSource.TimerSource && data.value !== null) {
        this.trackerActive.set(false);
        const timerActive = this.sharedTimerHandlerService.timerStatus === TimerStatus.Start;

        this.timerActive.set(timerActive);

        // Stop timer incrementation after clicking on "stop".
        if (!timerActive) {
          this.sharedClockHandlerService.resetClock();
        }
        return;
      }

      // If timer is present, return.
      const activeTimerTimeBlockId = this.sharedTimerHandlerService.timerActiveTBChanged$.value;
      if (
        activeTimerTimeBlockId !== null &&
        activeTimerTimeBlockId !== SharedActiveTimeBlockService.noActiveTimerTimeBlockId
      ) {
        return;
      }

      const trackerStatus = data.value;
      if (data.source === TimeBlockSource.TrackerSource) {
        if (trackerStatus === TrackerStatus.Pause || data.value === TrackerStatus.None) {
          this.sharedClockHandlerService.resetClock();
        }

        this.timerActive.set(false);
        this.trackerActive.set(
          data.value === TrackerStatus.Recording || data.value === TrackerStatus.Pause,
        );
        this.trackerStatus.set(trackerStatus);
      }
    });
  }

  ngAfterViewInit(): void {
    let subMenuOpened = false;
    this.parentListItem.forEach((el) => {
      if (el.nativeElement.classList.contains(this.sidebarService.currentRoute)) {
        if (el.nativeElement.classList.contains('has-submenu')) {
          this.renderer.addClass(el.nativeElement, 'expanded');
          subMenuOpened = true;
        }
        this.renderer.addClass(el.nativeElement, 'active');
      }
    });

    if (!subMenuOpened) {
      this.renderer.addClass(this.parentNavItem.nativeElement, 'all-sub-menus-closed');
    }

    this.initSidebarEvents();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public toggleMenu(parentListEl: HTMLElement): void {
    const item = parentListEl.getAttribute('data-item');
    this.sidebarService.closeMenus(item, this.parentListItem, this.parentNavItem);

    const classList = parentListEl.classList;
    if (classList.contains('expanded')) {
      parentListEl.classList.remove('expanded');
      this.renderer.addClass(this.parentNavItem.nativeElement, 'all-sub-menus-closed');
    } else {
      parentListEl.classList.add('expanded');
      this.renderer.removeClass(this.parentNavItem.nativeElement, 'all-sub-menus-closed');
    }
  }

  public setParentAsActive(parentListEl: HTMLElement): void {
    this.removeActiveClass();
    this.renderer.addClass(parentListEl, 'active');
  }

  public goTo(path: string, parentListEl: HTMLElement): void {
    this.removeActiveClass();
    void this.router.navigate([path]).then((success) => {
      if (success) {
        const item = parentListEl.getAttribute('data-item');
        this.sidebarService.closeMenus(item, this.parentListItem, this.parentNavItem);

        this.renderer.addClass(parentListEl, 'active');
      }
    });
  }

  public toggleTimer(): void {
    this.sharedTimerHandlerService.toggleTimer();
  }

  private removeActiveClass(): void {
    this.parentListItem.forEach((el) => {
      this.renderer.removeClass(el.nativeElement, 'active');
    });
  }

  private initSidebarEvents(): void {
    this.subs.sink = this.events.subscribe(() => {
      if (this.sidebarService.isCompressed) {
        this.renderer.addClass(this.aside, 'compressed');
      } else {
        this.renderer.removeClass(this.aside, 'compressed');
      }

      // this.renderer.removeClass(this.aside, 'collapse-sidebar');
      this.sidebarService.invertScale('none', this.mainPanel);
    });
  }
}
